<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'jobs-v2',
    path: '/jobs-v2',
    component: 'JobsV2',
    title: () => pgettext('Jobs|Title', 'Jobs'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobsV2',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'number',
      name: $fn.value.pgettext('Jobs', 'Number'),
      type: 'command',
      formatter: [
        {
          name: 'template',
          format: '#{{ number }}',
        },
        {
          name: 'link',
          href: '#',
        },
      ],
    },
    {
      field: 'parent_number',
      name: $fn.value.pgettext('Jobs', 'Parent'),
      type: 'command',
      formatter: [
        {
          name: 'template',
          format: '#{{ parent_number }}',
        },
        {
          name: 'link',
          href: '#',
        },
      ],
    },
    {
      field: 'status',
      name: $fn.value.pgettext('Jobs', 'Status'),
    },
    {
      field: 'name',
      name: $fn.value.pgettext('Jobs', 'Job type'),
    },
    {
      field: 'bulk_job',
      name: $fn.value.pgettext('Jobs', 'Bulk'),
      type: 'boolean',
      formatter: 'checkbox',
    },
    {
      field: 'key',
      name: $fn.value.pgettext('Jobs', 'Key'),
    },
    {
      field: 'queue',
      name: $fn.value.pgettext('Jobs', 'Queue'),
    },
    {
      field: 'submit_time',
      name: $fn.value.pgettext('Jobs', 'Submit time'),
      formatter: {
        name: 'datetime',
      },
    },
    {
      field: 'created_by',
      name: $fn.value.pgettext('Jobs', 'Submitter'),
      formatter: {
        name: 'user',
      },
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('browse_a_job');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Jobs|Title', 'Jobs'),
      icon: $config.value.jobs.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_74ipu5hfh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_e6iwflo3y_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_7v2vov88k_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_u7gpzoq1q_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'number';
  };
  const act_2248226175642056_showModal_kqf0jhzmm_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Job',
      props: {
        oid: $event.data.data.parent_id,
      },
    };
  };
  const inst_evh_2248226175642056_command_suizogpdw_getWhen = async (
    $event
  ) => {
    return (
      $event.data.column.id === 'parent_number' && $event.data.data.parent_id
    );
  };

  const act_7315092382398562_closeModal_74ipu5hfh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_74ipu5hfh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_419ebzv53 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_74ipu5hfh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_419ebzv53],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_64efuuzkg =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_fcqglmw0k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_64efuuzkg,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_fcqglmw0k],
    event: 'reload',
  });
  const act_2248226175642056_setData_e6iwflo3y = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_e6iwflo3y_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_myptm61cd =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_e6iwflo3y,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_myptm61cd],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_7v2vov88k =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_7v2vov88k_getActionArgs,
    });
  const inst_evh_2248226175642056_command_u7gpzoq1q =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_7v2vov88k,
      event: 'command',
      when: inst_evh_2248226175642056_command_u7gpzoq1q_getWhen,
      displayName: 'showModal',
    });
  const act_2248226175642056_showModal_kqf0jhzmm =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_kqf0jhzmm_getActionArgs,
    });
  const inst_evh_2248226175642056_command_suizogpdw =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_kqf0jhzmm,
      event: 'command',
      when: inst_evh_2248226175642056_command_suizogpdw_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_2248226175642056_command_u7gpzoq1q,
      inst_evh_2248226175642056_command_suizogpdw,
    ],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_close_419ebzv53,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_fcqglmw0k,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_myptm61cd,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_u7gpzoq1q,
    inst_evh_2248226175642056_command_suizogpdw,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_close_419ebzv53 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_419ebzv53.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_fcqglmw0k = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_fcqglmw0k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_myptm61cd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_myptm61cd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_u7gpzoq1q = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_u7gpzoq1q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_suizogpdw = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_suizogpdw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="JobsV2"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="jobs-v2"
      :headerTitle="pgettext('Jobs', 'Jobs')"
      :items="ds"
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <PageHelp path="/general/jobs.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
